
import moment from 'moment';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Image,
  List,
  Segment,
  Icon,
  Header,
  Divider, // Added Divider for visual separation
} from 'semantic-ui-react';
import { useQuery } from '@apollo/client';
import { useState } from 'react';

// Local imports
import { PATHS } from '../../../../paths';
import { formatDate } from '../../../../dates';

import { InterimStates } from '../../../../components/InterimStates';
import { PersonCourseAttendance } from '../../../../components/Protected/PersonCourseAttendance';
import { PersonBlockAttendance } from '../../../../components/Protected/PersonBlockAttendance';
import { UserPhoneNumber } from '../../../../components/UserPhoneNumber';

import { JoyyaHeader } from '../../../../components/JoyyaHeader';

// --- GraphQL Queries and Mutations ---
import { GET_PERSON } from '../data/GET_PERSON';

import { sortBlocksDecending } from '../utils/sortBlocksDecending';

import styles from './TeacherProfileView.module.css'

const TeacherProfileView = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const [editMode, setEditMode] = useState(false);

  const { loading, error, data } = useQuery(GET_PERSON, {
    variables: { personId: Number(id) },
    skip: !id || isNaN(Number(id)),
  });

  if (loading || error || !data?.user) {
    return (
      <InterimStates
        loading={loading}
        error={error}
        label="person's details"
      />
    );
  }

  const { user } = data;

  if (user.id == null) {
      return (
          <div>You don't have permission to view this</div>
      )
  }

  return (
      <>
        <JoyyaHeader />
          <div className='mobile-frame'>
          <div className={styles.overlay}>
              <div className={styles.back}>
                <Icon
                  name="arrow left"
                  size="large"
                  style={{ cursor: 'pointer', verticalAlign: 'baseline' }}
                  onClick={() => navigate(-1)}
                />
                <span className={styles.header}>Back</span>
              </div>
            <div className={styles.center}>
              <Image
                src={user.photoUrl}
                size="large"
                circular // Circular for profile photos
                className={styles.profileImage}
              />
              <span className={styles.name}>
                {user.name}
              </span>

              {(user.scriptName || user.preferredName) && (
                <div>
                  <span className={styles.otherName}>
                    {user.scriptName}{' '}
                    {user.preferredName && `(${user.preferredName})`}
                  </span>
                </div>
              )}
              {!user.isBeingViewedByBuddy &&
                <p className={styles.iden}>
                  ID: {user.identifier}
                </p>}
            </div>
          </div>

          <div>
            <div className={styles.infoLine}>
              <span>Address:</span>
              {user.address}
            </div>
            <div className={styles.infoLine}>
              <span>Phone:
                <span className={styles.edit} onClick={() => setEditMode(!editMode)}> ({editMode ? "done": "edit" })</span>
              </span>
              <UserPhoneNumber userId={user.id} inEditMode={editMode} />
            </div>
            <div className={styles.infoLine}>
              <span>Date of Birth:</span>
              {formatDate(user.dob)}
            </div>
            <div className={styles.infoLine}>
              <span>Age: </span>
              {moment().diff(moment(Number(user.dob)), 'years')} years,{' '}
              {moment().diff(moment(Number(user.dob)), 'months') % 12} months
            </div>
            <div className={styles.infoLine}>
              <span>Attendance:</span>
              {Math.round(user.attendancePercentage * 100)}%
            </div>
            <div className={styles.infoLine}>
              <span>Picture Permission:</span>
              {user.picturePermission ? 'Allowed' : 'Not Allowed'}
            </div>
            <div className={styles.infoLine}>
              <span>Truancy:</span>
              {user.truency}
            </div>
            <div className={styles.infoLine}>
              <span>Registration Docs:</span>
              {user.haveRegistrationDocument ? 'Yes ' : 'No '}
              {user.registrationDocumentUrl && (
                <a
                  target="_blank"
                  href={user.registrationDocumentUrl}
                  rel="noopener noreferrer"
                >
                  - View
                </a>
              )}
            </div>
          </div>

          <Header as="h2" className={styles.segmentTitle}>
              Current Block Attendance
          </Header>

            {user.currentCourseAttendances.length === 0 ? (
              <p>Not currently enrolled in any classes.</p>
            ) : (
              <List divided relaxed>
                  {user.currentCourseAttendances.filter(({ course }: any) => {
                    return course && course.SKU.endsWith('APR2026');
                })
                  .map(({ course, attendance }: any) => (
                  <List.Item key={course.id}>
                    <List.Content>
                      <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                        {course.name}
                      </List.Header>
                      <List.Description>
              {!user.isBeingViewedByBuddy && (<>
                      {course.SKU} <br/>
              </>)}
                      Attendance: {attendance.reduce((acc: number, a: any) => acc + (a.status === 'PRESENT' ? 1 : 0), 0)}/{attendance.length} <br/>
                         {attendance.map((a: any) => (
                            <span key={a.id} style={{ marginRight: '0.5em' }}>
                                {a.status === 'ABSENT' ? '❌' : a.status === 'PRESENT' ? '✅' : '❓'}
                            </span>
                         ))}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                ))}
                </List>
            )}

          {/* --- Classes Section --- */}
          {!user.isBeingViewedByBuddy && <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
            <Header as="h2">
              <Icon name="book" /> Past Classes
            </Header>
            <Divider />
            {user.courses
                 .length === 0 ? (
              <p>Not currently enrolled in any classes.</p>
            ) : (
              <List divided relaxed>
                {Object.entries(
                  user.courses
                 .reduce((acc: any, c: any) => {
                    const sku = c.SKU.slice(-7);
                    if (!acc[sku]) {
                      acc[sku] = [];
                    }
                    acc[sku].push(c);
                    return acc;
                  }, {})
                )
                  .sort(sortBlocksDecending as any)
                  .map(([sku, courses]: [string, any]) => (
                    <List.Item key={sku}>
                      <Header as="h4" style={{ marginBottom: '0.5em' }}>
                        {sku}
                      </Header>
                      {courses.length > 1 && (<>
                                Block Attendance:{' '}
                                <PersonBlockAttendance
                                  personId={Number(id)}
                                  blockCode={sku}
                                />
                          </>
                      )}

                      <List.List>
                      {courses.map((course: any) => (
                          <List.Item
                            key={course.id}>
                            <List.Content>
                              <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                                {course.name}
                              </List.Header>
                              <List.Description>
                      {course.SKU} <br/>
                                Attendance:{' '}
                                <PersonCourseAttendance
                                  personId={Number(id)}
                                  courseId={course.id}
                                />
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List.List>
                    </List.Item>
                  ))}
              </List>
            )}
          </Segment>
          }

          {/* --- Teaching Section --- */}
          {user.coursesTeaching.length > 0 && (
            <Segment raised style={{ padding: '2em', marginBottom: '2em' }}>
              <Header as="h2">
                <Icon name="graduation cap" /> Classes Teaching
              </Header>
              <Divider />
              <List divided relaxed>
                {Object.entries(
                  user.coursesTeaching.reduce((acc: any, c: any) => {
                    const sku = c.SKU.slice(-7);
                    if (!acc[sku]) {
                      acc[sku] = [];
                    }
                      console.log('c', c);
                    acc[sku].push(c);
                    return acc;
                  }, {})
                )
                  .sort(sortBlocksDecending as any)
                  .map(([sku, courses]: [string, any]) => (
                    <List.Item key={sku}>
                      <Header as="h4" style={{ marginBottom: '0.5em' }}>
                        {sku}
                      </Header>
                      <List.List>
                        {courses.map((course: any) => (
                          <List.Item key={course.id}>
                            <List.Content>
                              <List.Header as={Link} to={`${PATHS.CLASSES}/${course.id}`}>
                                {course.name}
                              </List.Header>
                              <List.Description>
                                Teacher - {course.SKU}
                              </List.Description>
                            </List.Content>
                          </List.Item>
                        ))}
                      </List.List>
                    </List.Item>
                  ))}
              </List>
            </Segment>
          )}
        </div>
    </>
  );
};

export { TeacherProfileView };
